import type { Coordinates } from '../types/company';

/**
 * Adresse physique du siège : saisie en texte (avenue, numéro, quartier,
 * commune), portée par `siegeSocial.address`. C'est elle qui s'imprime sur
 * les factures, reçus et tickets des apps.
 *
 * La carte pose d'elle-même un libellé (« Position GPS... », coordonnées,
 * ville la plus proche) qui n'est pas une adresse : il ne remplace jamais une
 * adresse saisie et ne s'affiche pas comme telle.
 */
export function isMapPickerLabel(address: string | null | undefined): boolean {
  const s = (address ?? '').trim();
  if (!s) return true;
  if (/^-?\d{1,3}(\.\d+)?\s*,\s*-?\d{1,3}(\.\d+)?$/.test(s)) return true;
  if (/^position (gps|temporaire)/i.test(s)) return true;
  return /^[\p{L}' -]{2,40}, République Démocratique du Congo$/u.test(s);
}

/** Adresse saisie du siège, ou chaîne vide. */
export function typedSiegeAddress(siege: Coordinates | null | undefined): string {
  const a = siege?.address?.trim() ?? '';
  return isMapPickerLabel(a) ? '' : a;
}

/** Siège avec l'adresse saisie, coordonnées conservées. */
export function withSiegeAddress(siege: Coordinates | null | undefined, address: string): Coordinates | null {
  const text = address.trim();
  if (!siege && !text) return null;
  return { ...(siege ?? ({} as Coordinates)), address: text || undefined } as Coordinates;
}

/** Point choisi sur la carte : l'adresse saisie reste celle du siège. */
export function withPickedLocation(siege: Coordinates | null | undefined, picked: Coordinates): Coordinates {
  const typed = typedSiegeAddress(siege);
  return typed ? { ...picked, address: typed } : picked;
}

/** Coordonnées réellement posées sur la carte (sinon adresse seule). */
export function hasCoordinates(siege: Coordinates | null | undefined): boolean {
  return Number.isFinite(siege?.latitude) && Number.isFinite(siege?.longitude);
}
